import { useEffect, useState, useCallback } from "react";
import { Loader2, ShieldCheck, Check, X, Pencil, ExternalLink, Inbox } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { OVERLINE, H1, Panel, btnPrimary, useAdmin } from "@/components/admin/ui";

const CONF = {
  verified: { label: "Verified", cls: "bg-[#2E7D46]/12 text-[#2E7D46]" },
  high: { label: "High confidence", cls: "bg-[#3B5B8C]/12 text-[#3B5B8C]" },
  review: { label: "Review needed", cls: "bg-[#B7761F]/15 text-[#B7761F]" },
  error: { label: "Error", cls: "bg-[#B04A3A]/15 text-[#B04A3A]" },
};
const STATUSES = ["pending", "approved", "rejected", "corrected", "all"];

function CorrectModal({ it, onClose, onDone }) {
  const [val, setVal] = useState(it.new_value ?? "");
  const [conf, setConf] = useState("high");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const inp = "w-full border border-[#E5E2DC] bg-white px-3 py-2 mt-1 text-sm outline-none focus:border-[#1F2A22]";
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.adminReviewAction(it.id, { action: "correct", corrected: { new_value: val, confidence: conf }, note });
      toast.success(r.applied ? "Corrected & published to live data" : `Corrected — ${r.apply_result || "recorded"}`);
      onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50" data-testid="correct-modal">
      <div className="absolute inset-0 bg-[#1F2A22]/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-[#F9F8F6] p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-lg text-[#1F2A22]">Correct value</h3>
          <button onClick={onClose} aria-label="Close" data-testid="correct-close"><X size={18} className="text-[#5C6B61]" /></button>
        </div>
        <p className="text-xs text-[#5C6B61] mb-3">{it.jurisdiction} · {it.field}. Detected value: <span className="font-medium">{String(it.new_value ?? "—")}</span>. Correcting a valid draw value publishes it to the live data.</p>
        <label className="block"><span className="text-[11px] uppercase tracking-wide text-[#5C6B61]">Corrected value</span><input value={val} onChange={(e) => setVal(e.target.value)} className={inp} data-testid="correct-value" /></label>
        <label className="block mt-3"><span className="text-[11px] uppercase tracking-wide text-[#5C6B61]">Confidence</span>
          <select value={conf} onChange={(e) => setConf(e.target.value)} className={inp} data-testid="correct-confidence">
            <option value="verified">Verified</option><option value="high">High confidence</option><option value="review">Review needed</option>
          </select></label>
        <label className="block mt-3"><span className="text-[11px] uppercase tracking-wide text-[#5C6B61]">Note (optional)</span><input value={note} onChange={(e) => setNote(e.target.value)} className={inp} data-testid="correct-note" /></label>
        <button onClick={submit} disabled={busy} className={`${btnPrimary} w-full mt-4`} data-testid="correct-submit">{busy ? <Loader2 size={15} className="animate-spin" /> : <Pencil size={15} />} Save correction</button>
      </div>
    </div>
  );
}

function ReviewCard({ it, canEdit, onDone }) {
  const [busy, setBusy] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const c = CONF[it.confidence] || CONF.review;
  const act = async (action) => {
    setBusy(true);
    try {
      const r = await api.adminReviewAction(it.id, { action });
      toast.success(action === "approve" ? (r.applied ? "Approved & published to live data" : "Approved") : "Marked rejected");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="border border-[#E5E2DC] bg-white p-4" data-testid={`review-item-${it.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-serif text-[#1F2A22]">{it.jurisdiction}</span>
            <span className="text-[11px] uppercase tracking-wide text-[#5C6B61]">{(it.change_type || "").replace(/_/g, " ")}</span>
          </div>
          <p className="text-sm text-[#1F2A22] mt-1">{it.summary}</p>
          <p className="text-xs text-[#5C6B61] mt-1">
            <span className="font-medium">{it.field}</span>: <span className="line-through">{String(it.old_value ?? "—")}</span> → <span className="text-[#1F2A22]">{String(it.new_value ?? "—")}</span>
          </p>
          {it.validation_issues?.length > 0 && <p className="text-[11px] text-[#B04A3A] mt-1">⚠ {it.validation_issues.join(", ")}</p>}
          {it.source_url && <a href={it.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#3B5B8C] hover:underline mt-1.5" data-testid={`review-source-${it.id}`}>Official source <ExternalLink size={11} /></a>}
        </div>
        <span className={`shrink-0 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 ${c.cls}`}>{c.label}</span>
      </div>
      {canEdit && it.status === "pending" && (
        <div className="flex gap-2 mt-3 pt-3 border-t border-[#E5E2DC]">
          <button onClick={() => act("approve")} disabled={busy} className="inline-flex items-center gap-1.5 bg-[#1F2A22] text-white px-3 py-1.5 text-xs font-semibold hover:bg-[#2E7D46]" data-testid={`review-approve-${it.id}`}><Check size={13} /> Approve</button>
          <button onClick={() => act("reject")} disabled={busy} className="inline-flex items-center gap-1.5 border border-[#E5E2DC] text-[#5C6B61] px-3 py-1.5 text-xs font-semibold hover:border-[#B04A3A] hover:text-[#B04A3A]" data-testid={`review-reject-${it.id}`}><X size={13} /> Reject</button>
          <button onClick={() => setCorrecting(true)} disabled={busy} className="inline-flex items-center gap-1.5 border border-[#E5E2DC] text-[#5C6B61] px-3 py-1.5 text-xs font-semibold hover:border-[#1F2A22] hover:text-[#1F2A22]" data-testid={`review-correct-${it.id}`}><Pencil size={13} /> Correct</button>
        </div>
      )}
      {it.status !== "pending" && <p className="text-[11px] text-[#5C6B61] mt-2" data-testid={`review-status-${it.id}`}><span className="capitalize">{it.status}</span>{it.reviewed_by ? ` · ${it.reviewed_by}` : ""}{it.applied ? " · published" : ""}</p>}
      {correcting && <CorrectModal it={it} onClose={() => setCorrecting(false)} onDone={onDone} />}
    </div>
  );
}

export default function AdminReviewQueue() {
  const { admin } = useAdmin();
  const canEdit = admin?.permissions?.includes("states");
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("pending");
  const load = useCallback(() => { if (canEdit) api.adminReviewQueue({ status }).then(setData).catch(() => setData(null)); }, [canEdit, status]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#1F2A22]" /></div>;
  const conf = data.counts?.by_confidence || {};

  return (
    <div className="p-6 md:p-8 max-w-4xl" data-testid="admin-review-queue">
      <div><span className={OVERLINE}>HomeRange Data AI</span><h1 className={`${H1} mt-1`}>Review Queue</h1></div>
      <p className="text-sm text-[#5C6B61] mt-2">Changes the Data AI detected on official sources. Verified & high-confidence items are safe to approve; review & error items need a careful look before publishing.</p>

      <div className="flex flex-wrap gap-2 mt-5">
        {Object.keys(CONF).map((k) => (
          <span key={k} className={`text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 ${CONF[k].cls}`} data-testid={`conf-count-${k}`}>{CONF[k].label}: {conf[k] || 0}</span>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mt-5">
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1.5 text-xs border capitalize ${status === s ? "bg-[#1F2A22] text-white border-[#1F2A22]" : "border-[#E5E2DC] text-[#5C6B61] hover:border-[#1F2A22]"}`} data-testid={`review-filter-${s}`}>{s}</button>
        ))}
      </div>

      <div className="mt-6 space-y-3">
        {data.items.length === 0 ? (
          <Panel><div className="py-10 text-center text-[#5C6B61]" data-testid="review-empty"><Inbox className="mx-auto mb-2 text-[#C7C2B8]" /><ShieldCheck className="inline mr-1 text-[#2E7D46]" size={14} /> Nothing in this queue. The Data AI flags changes here as they&apos;re detected.</div></Panel>
        ) : (
          data.items.map((it) => <ReviewCard key={it.id} it={it} canEdit={canEdit} onDone={load} />)
        )}
      </div>
    </div>
  );
}
